import React, { useMemo, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { getEarnedMediaIcon } from "./earnedMediaIcons";
import CoverageBar from "./CoverageBar";

const PRIORITY_STYLES = {
	critical: "monitoring__analysis-strategy-earned-priority--critical",
	high: "monitoring__analysis-strategy-earned-priority--high",
	medium: "monitoring__analysis-strategy-earned-priority--medium",
	low: "monitoring__analysis-strategy-earned-priority--low",
};

function normalizeActionItem(action) {
	if (typeof action === "string") return action.trim();
	if (action && typeof action === "object") {
		return String(action.text ?? action.action ?? action.description ?? "").trim();
	}
	return "";
}

function normalizeEarnedMediaRec(rec) {
	const recommendedActions = (
		Array.isArray(rec?.recommended_actions)
			? rec.recommended_actions
			: Array.isArray(rec?.recommendedActions)
				? rec.recommendedActions
				: []
	)
		.map(normalizeActionItem)
		.filter(Boolean);
	const referenceUrls = (
		Array.isArray(rec?.reference_urls)
			? rec.reference_urls
			: Array.isArray(rec?.referenceUrls)
				? rec.referenceUrls
				: []
	)
		.map((url) => String(url ?? "").trim())
		.filter(Boolean);

	return {
		...rec,
		recommended_actions: recommendedActions,
		reference_urls: referenceUrls,
	};
}

function EarnedMediaCard({ rec }) {
	const [expanded, setExpanded] = useState(false);
	const normalized = useMemo(() => normalizeEarnedMediaRec(rec), [rec]);
	const icon = getEarnedMediaIcon(normalized.platform, normalized.channel_type);
	const hasActionPlan =
		normalized.recommended_actions.length > 0 || normalized.reference_urls.length > 0;

	return (
		<div className="monitoring__analysis-strategy-card monitoring__analysis-strategy-earned-card">
			<div className="monitoring__analysis-strategy-earned-card-head">
				<div className="d-flex align-items-center gap-2 min-w-0">
					<span
						className={`monitoring__earned-icon ${icon.className}`}
						aria-hidden
					>
						{icon.label}
					</span>
					<span className="fw-semibold small text-truncate">{normalized.platform}</span>
				</div>
				<span
					className={`monitoring__analysis-strategy-earned-priority ${PRIORITY_STYLES[normalized.priority] ?? PRIORITY_STYLES.low
						}`}
				>
					{normalized.priority}
				</span>
			</div>
			<div className="monitoring__analysis-strategy-card-body pt-0">
				<span className="monitoring__analysis-strategy-category-badge mb-2 d-inline-block">
					{normalized.channel_type}
				</span>
				<p className="small text-muted mb-2">{normalized.objective}</p>
				<div className="mb-2">
					<span className="small text-muted text-uppercase d-block mb-1">Influences</span>
					<CoverageBar impact={normalized.provider_impact} showLabel={false} />
				</div>
				<button
					type="button"
					onClick={() => setExpanded((e) => !e)}
					className="monitoring__analysis-strategy-show-more btn btn-link btn-sm p-0"
					aria-expanded={expanded}
				>
					{expanded ? "Hide" : "View"} Action Plan{" "}
					{expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
				</button>
				{expanded && (
					<div className="border-top pt-2 mt-2 monitoring__analysis-strategy-earned-action-plan">
						{normalized.recommended_actions.length > 0 ? (
							<ol className="monitoring__analysis-strategy-signal-ol-list">
								{normalized.recommended_actions.map((action, i) => (
									<li
										key={`${action}-${i}`}
										className="monitoring__analysis-strategy-signal-ol-list-item"
									>
										{action}
									</li>
								))}
							</ol>
						) : null}
						{normalized.reference_urls.length > 0 ? (
							<div className="d-flex flex-column gap-1 mb-2 ">
								{normalized.reference_urls.map((url) => (
									<a
										key={url}
										href={url}
										target="_blank"
										rel="noopener noreferrer"
										className="monitoring__analysis-strategy-earned-link small text-truncate"
									>
										{url}
									</a>
								))}
							</div>
						) : null}
						{!hasActionPlan ? (
							<p className="small text-muted mb-2">
								No detailed action steps were generated for this recommendation yet.
							</p>
						) : null}
						<p className="small text-muted fst-italic mb-0">Manual/strategic action.</p>
					</div>
				)}
			</div>
		</div>
	);
}

export default function EarnedMediaGrid({ items }) {
	if (items.length === 0) {
		return (
			<p className="monitoring__analysis-strategy-empty">
				No earned media recommendations.
			</p>
		);
	}

	return (
		<div className="monitoring__analysis-strategy-earned-grid">
			{items.map((rec, index) => (
				<EarnedMediaCard key={rec.id ?? `earned-${index}`} rec={rec} />
			))}
		</div>
	);
}
